import React, { useRef, useState, useEffect } from 'react';
import { Modal, message, Spin, Button } from 'antd';
import { CommonForm, FileUpload, FormConfig } from '@jd/x-coreui';
import { repairApi } from '@/fetch/bussiness/repair';
import './index.scss';
import { isEmpty } from '@/utils/util';
import { HttpStatusCode } from '@/fetch/core/constant';
import { videoExtensions, imageExtensions } from '@/utils/util';

const formConfig: FormConfig = {
  fields: [
    {
      type: 'radioGroup',
      fieldName: 'isInfluenceOperation',
      label: '问题影响',
      validatorRules: [{ required: true, message: '请选择问题影响' }],

      options: [
        { label: '影响运营', value: 1 },
        { label: '不影响运营', value: 0 },
      ],
    },
    {
      type: 'textarea',
      fieldName: 'description',
      label: '问题描述',
      validatorRules: [
        { required: true, message: '请输入问题描述' },
        { max: 50, message: '问题描述最多50字' },
      ],

      placeholder: '请输入问题描述',
      maxLength: 50,
      showCount: true,
    },
    {
      type: 'dateTime',
      fieldName: 'appointmentRepairTime',
      label: '预约维修时间',
      format: 'YYYY-MM-DD HH:mm',
    },
  ],
};

interface CreateRepairOrderProps {
  vehicleName: string;
  issueNumber?: string;
  closeDropdown: () => void;
}

const CreateRepairOrder = ({
  vehicleName,
  issueNumber,
  closeDropdown,
}: CreateRepairOrderProps) => {
  const [repairModelVisible, setRepairModelVisible] = useState(false);
  const formRef = useRef<any>();
  const [uploading, setUploading] = useState(false);
  const [formatFormConfig, setFormatFormConfig] = useState<any>(formConfig);
  const [fileList, setFileList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (repairModelVisible) {
      fetchHardwareTypes();
    }
  }, [repairModelVisible]);

  const fetchHardwareTypes = async () => {
    try {
      const res = await repairApi.getHardwareTypes();
      if (res && res.code === HttpStatusCode.Success && !isEmpty(res.data)) {
        const options = res.data.map((item: any) => ({
          label: item.hardwareTypeName,
          value: item.hardwareTypeId,
        }));
        const dynamicFormConfig = {
          ...formConfig,
          fields: formConfig.fields.map((item) => {
            if (item.fieldName === 'requireHardwareTypeIds') {
              return {
                ...item,
                options: options,
              };
            }
            return item;
          }),
        };
        setFormatFormConfig(dynamicFormConfig);
      }
    } catch (error) {
      message.error('获取维修位置失败');
    }
  };

  const handleOk = async () => {
    if (!vehicleName) {
      message.error('车牌号异常, 请退出重试');
      return;
    }
    if (uploading) {
      message.error('请等待文件上传完成');
      return;
    }
    try {
      setLoading(true);
      if (formRef.current?.getFieldValue('description')?.trim() === '') {
        message.error('请输入问题描述');
        return;
      }
      const values = await formRef.current?.validateFields();
      const pictureList: string[] = [];
      let video = '';
      fileList.forEach((fileKey: any) => {
        const isVideo = videoExtensions.some((ext) =>
          fileKey?.toLowerCase()?.endsWith(ext)
        );
        if (isVideo) {
          video = fileKey || '';
        } else {
          pictureList.push(fileKey || '');
        }
      });
      const requestData = {
        vehicleName,
        description: values.description || null,
        pictureList,
        video,
        isInfluenceOperation: values.isInfluenceOperation,
        issueNumber: issueNumber || '',
        appointmentRepairTime: values.appointmentRepairTime
          ? values.appointmentRepairTime?.format('YYYY-MM-DD HH:mm:ss')
          : null,
      };
      const res = await repairApi.addVehicleRequire(requestData);
      if (res && res.code === HttpStatusCode.Success) {
        message.success('维修单创建成功');
        handleCancel();
      } else {
        throw new Error(res?.message || '创建失败');
      }
    } catch (err) {
      const error = err as any;
      message.error(error?.message || '请检查表单信息');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRepairModelVisible(false);
  };

  return (
    <>
      <button
        className="issue-command-btn"
        onClick={() => {
          setRepairModelVisible(true);
          closeDropdown();
        }}
      >
        创建维修单
      </button>
      <Modal
        title={`车号 ${vehicleName}`}
        open={repairModelVisible}
        onCancel={handleCancel}
        width={1200}
        destroyOnClose
        footer={
          <div
            style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}
          >
            <Button onClick={handleCancel}>取消</Button>
            <Button onClick={handleOk} loading={loading} type="primary">
              确定
            </Button>
          </div>
        }
      >
        <div className="repair-order-modal">
          <CommonForm
            formConfig={formatFormConfig}
            getFormInstance={(ref) => (formRef.current = ref)}
            layout="horizontal"
            formType="edit"
            theme="dark"
          />
          <div className="upload-container">
            <div className="media-field">
              <span>*</span>上传图片或视频
            </div>
            <Spin spinning={uploading}>
              <FileUpload
                maxCount={4}
                accept="image/*,video/*"
                fileListType={['picture', 'video']}
                getPreSignatureUrl={`${location.protocol}//${process.env.JDX_APP_CLOUD_FETCH_DOMAIN}/infrastructure/oss/getPreUrl`}
                LOPDN={process.env.JDX_APP_REQUEST_HEADER!}
                bucketName="rover-operation"
                needMd5Validete={false}
                maxFileSize={50}
                unit="MB"
                uploadBtn={{
                  type: 'dragger',
                  btnText: '添加文件',
                  draggerConfig: {
                    title: '点击或拖拽文件到此处上传',
                    hint: '支持1个视频或3张图片',
                  },
                }}
                onEnd={(fileKey: string) => {
                  console.log('上传成功, fileKey', fileKey);
                  const newFileList = [...fileList, fileKey];
                  setFileList(newFileList);
                }}
                onError={(error: Error, body?: Object) => {
                  message.error('文件上传失败');
                }}
                onDelete={(fileKey: string) => {
                  const newFileList = fileList.filter((key) => key !== fileKey);
                  setFileList(newFileList);
                }}
                extensionValidate={(fileName: string) => {
                  const isVideo = videoExtensions.some((ext) =>
                    fileName.toLowerCase().endsWith(ext)
                  );
                  const isImage = imageExtensions.some((ext) =>
                    fileName.toLowerCase().endsWith(ext)
                  );
                  if (isVideo) {
                    const videoCount = fileList.filter((file: any) =>
                      videoExtensions.some((ext) =>
                        file?.toLowerCase().endsWith(ext)
                      )
                    ).length;
                    if (videoCount >= 1) {
                      return {
                        isValid: false,
                        message: '最多只能上传1个视频',
                      };
                    }
                  } else if (isImage) {
                    const imageCount = fileList.filter((file: any) =>
                      imageExtensions.some((ext) =>
                        file?.toLowerCase().endsWith(ext)
                      )
                    ).length;
                    if (imageCount >= 3) {
                      return {
                        isValid: false,
                        message: '最多只能上传3张照片',
                      };
                    }
                  }
                  return true;
                }}
                onUploadingChange={setUploading}
              />
            </Spin>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default React.memo(CreateRepairOrder);
